import { Container, Graphics } from "pixi.js";
import {
  pathDistanceOfTile,
  type SimulationState,
  STORAGE_TILE,
  tileAtPathDistance,
  UNLOAD_DURATION_SEC,
  useSimulationStore,
  type Work,
} from "@/features/simulation/public";
import type { StageLayers } from "@/lib/pixi/createApp";
import { depthOf, MAP_COLS, MAP_ROWS, type TilePos, tileToScreen } from "@/lib/pixi/isoCoords";
import {
  CONVEYOR_BELT_HEIGHT_PX,
  createArmBase,
  createArmReach,
  createPressBeam,
  createPressHead,
  createPressNorthLeg,
  createPressSouthLeg,
  createTransportRobot,
  createUnloadZone,
  createWorkSprite,
  drawConveyorSegment,
  drawIsoTile,
  drawOccupationTile,
  PALETTE,
} from "@/lib/pixi/textures";

/**
 * 工場マップの multi-instance 描画層 (P3)。
 *
 * scene 上の N 個のコンベア / プレス / アーム / 搬送ロボを id でマップ管理し、
 * tick 毎に state の対応ランタイム (machines / arms / transports) でアニメーション。
 *
 * scene が変更されたときは FactoryMap が PixiStage ごと key で再マウントするため、
 * このレンダラはそのとき再初期化される。
 */

export interface RendererHandle {
  update(state: SimulationState, dtMs: number): void;
  dispose(): void;
}

interface WorkSprite {
  root: Container;
  body: Graphics;
  lastPhase: Work["phase"];
}

interface ConveyorTile {
  conveyorId: string;
  tile: TilePos;
  graphic: Graphics;
  /** path 末端 (バッファ) かどうか */
  isEnd: boolean;
}

interface PressGraphics {
  pressId: string;
  position: TilePos;
  head: Graphics;
  beam: Container;
  northLeg: Graphics;
  southLeg: Graphics;
}

interface ArmGraphics {
  armId: string;
  container: Container;
  reach: Graphics;
  heldSprite: Graphics | null;
}

interface TransportGraphics {
  transportId: string;
  container: Container;
  cargoSprites: Map<string, Graphics>;
  activeTile: Graphics;
}

export function createFactoryRenderer(layers: StageLayers, initialState: SimulationState): RendererHandle {
  const scene = initialState.scene;

  // --- 静的: 床 ---
  drawGround(layers.groundLayer);

  // --- 静的: 占有タイル (press / arm / transport route 等) ---
  drawOccupationTiles(layers.groundLayer, initialState);
  drawUnloadArea(layers.groundLayer);

  // --- 動的: コンベアタイル ---
  const conveyorTiles: ConveyorTile[] = [];
  for (const c of scene.conveyors) {
    for (let i = 0; i < c.path.length; i++) {
      const tile = c.path[i];
      if (!tile) continue;
      const g = new Graphics();
      const { x, y } = tileToScreen(tile.col, tile.row);
      g.position.set(x, y);
      g.zIndex = depthOf(tile.col, tile.row) * 10;
      layers.entityLayer.addChild(g);
      conveyorTiles.push({ conveyorId: c.id, tile, graphic: g, isEnd: i === c.path.length - 1 });
    }
  }
  let conveyorPhaseSec = 0;

  // --- 動的: プレス (各プレスを 3 オブジェクト構成で描画) ---
  const pressGraphicsList: PressGraphics[] = [];
  for (const press of scene.presses) {
    const pressTile = press.position;
    const northLeg = createPressNorthLeg();
    const northTile = { col: pressTile.col, row: pressTile.row - 1 };
    {
      const { x, y } = tileToScreen(northTile.col, northTile.row);
      northLeg.position.set(x, y);
      northLeg.zIndex = depthOf(northTile.col, northTile.row) * 10;
      layers.entityLayer.addChild(northLeg);
    }
    const southLeg = createPressSouthLeg();
    const southTile = { col: pressTile.col, row: pressTile.row + 1 };
    {
      const { x, y } = tileToScreen(southTile.col, southTile.row);
      southLeg.position.set(x, y);
      southLeg.zIndex = depthOf(southTile.col, southTile.row) * 10 + 11;
      layers.entityLayer.addChild(southLeg);
    }
    const beam = new Container();
    beam.addChild(createPressBeam());
    const head = createPressHead();
    beam.addChild(head);
    {
      const { x, y } = tileToScreen(pressTile.col, pressTile.row);
      beam.position.set(x, y);
    }
    beam.zIndex = depthOf(pressTile.col, pressTile.row) * 10 + 50;
    layers.entityLayer.addChild(beam);
    pressGraphicsList.push({ pressId: press.id, position: pressTile, head, beam, northLeg, southLeg });
  }

  // --- 動的: アーム (各アームに container + reach) ---
  const armGraphicsList: ArmGraphics[] = [];
  for (const arm of scene.arms) {
    const container = new Container();
    container.addChild(createArmBase());
    const reach = createArmReach();
    reach.position.set(0, -6);
    container.addChild(reach);
    const { x, y } = tileToScreen(arm.position.col, arm.position.row);
    container.position.set(x, y);
    container.zIndex = depthOf(arm.position.col, arm.position.row) * 10 + 11;
    layers.entityLayer.addChild(container);
    armGraphicsList.push({ armId: arm.id, container, reach, heldSprite: null });
  }

  // --- 動的: 搬送ロボ (各搬送ロボに container + active tile + cargo sprites) ---
  const transportGraphicsList: TransportGraphics[] = [];
  for (const t of scene.transports) {
    const container = new Container();
    container.addChild(createTransportRobot());
    const { x, y } = tileToScreen(t.parking.col, t.parking.row);
    container.position.set(x, y);
    layers.entityLayer.addChild(container);

    const activeTile = new Graphics();
    drawOccupationTile(activeTile, PALETTE.occupationTransportActive, 0.6, PALETTE.occupationTransportActive, 1);
    activeTile.position.set(x, y);
    layers.groundLayer.addChild(activeTile);

    transportGraphicsList.push({
      transportId: t.id,
      container,
      cargoSprites: new Map(),
      activeTile,
    });
  }

  // --- 動的: ワーク (id でキャッシュ) ---
  const workSprites = new Map<string, WorkSprite>();

  function update(state: SimulationState, dtMs: number): void {
    const conveyorSpeed = useSimulationStore.getState().params.conveyorSpeed;
    const anyPressBusy = state.machines.some((m) => m.busy);
    if (!state.conveyorStopped && !anyPressBusy) {
      conveyorPhaseSec += (dtMs / 1000) * conveyorSpeed;
    }
    const phase = conveyorPhaseSec - Math.floor(conveyorPhaseSec);

    // コンベアタイルのアニメーション + オーバーレイ
    const pressOverlay = { color: PALETTE.occupationPress, alpha: 0.35 };
    const bufferOverlay = { color: PALETTE.occupationBuffer, alpha: 0.32 };
    const pressTilesByPosition = new Map<string, true>();
    for (const press of state.scene.presses) {
      pressTilesByPosition.set(`${press.position.col},${press.position.row}`, true);
    }
    for (const ct of conveyorTiles) {
      const key = `${ct.tile.col},${ct.tile.row}`;
      let overlay: { color: number; alpha: number } | undefined;
      if (pressTilesByPosition.has(key)) overlay = pressOverlay;
      else if (ct.isEnd) overlay = bufferOverlay;
      drawConveyorSegment(ct.graphic, phase, overlay);
    }

    // プレスヘッドのアニメーション (各プレス独立)
    // 振幅は 11 px。ビーム底 (≒ y=-22.5) 付近からワーク底 (≒ y=-7) までストロークする。
    // 旧実装は 5 px (-28→-23) しか動かず、しかもアイドル位置が完全にビーム内部に
    // 隠れていたため "プレスが動いていない" と認識されていた。
    for (const pg of pressGraphicsList) {
      const press = state.machines.find((m) => m.id === pg.pressId);
      if (!press) continue;
      const headUp = -28;
      const headDown = -17;
      const downAmount = press.busy ? Math.sin(press.progress * Math.PI) : 0;
      pg.head.position.set(0, headUp + downAmount * (headDown - headUp));
    }

    // アーム回転 (各アーム独立)
    for (const ag of armGraphicsList) {
      const armRuntime = state.arms.find((a) => a.id === ag.armId);
      if (!armRuntime) continue;
      ag.reach.rotation = armRuntime.armAngle;

      // 持っているワーク (heldSprite)
      if (armRuntime.heldWorkId !== null) {
        if (!ag.heldSprite) {
          ag.heldSprite = createWorkSprite("pressed");
          ag.container.addChild(ag.heldSprite);
        }
        const len = 22;
        ag.heldSprite.position.set(Math.sin(armRuntime.armAngle) * len, -6 - Math.cos(armRuntime.armAngle) * len);
      } else if (ag.heldSprite) {
        ag.container.removeChild(ag.heldSprite);
        ag.heldSprite.destroy();
        ag.heldSprite = null;
      }
    }

    // 搬送ロボ位置 + cargo (各搬送ロボ独立)
    for (const tg of transportGraphicsList) {
      const transportRuntime = state.transports.find((t) => t.id === tg.transportId);
      const transportScene = state.scene.transports.find((t) => t.id === tg.transportId);
      if (!transportRuntime || !transportScene) continue;

      const tscreen = tileToScreen(transportRuntime.position.col, transportRuntime.position.row);
      tg.container.position.set(tscreen.x, tscreen.y);
      tg.container.zIndex = depthOf(transportRuntime.position.col, transportRuntime.position.row) * 10;

      // active tile highlight
      const activeCol = Math.round(transportRuntime.position.col);
      const activeRow = Math.round(transportRuntime.position.row);
      const aScreen = tileToScreen(activeCol, activeRow);
      tg.activeTile.position.set(aScreen.x, aScreen.y);

      // 不要 cargo スプライト掃除
      const currentCargoSet = new Set(transportRuntime.cargo);
      for (const [id, sprite] of tg.cargoSprites) {
        if (!currentCargoSet.has(id)) {
          layers.entityLayer.removeChild(sprite);
          sprite.destroy();
          tg.cargoSprites.delete(id);
        }
      }

      transportRuntime.cargo.forEach((id, slot) => {
        let sprite = tg.cargoSprites.get(id);
        if (!sprite) {
          sprite = createWorkSprite("done");
          layers.entityLayer.addChild(sprite);
          tg.cargoSprites.set(id, sprite);
        }
        const stackOffset = slot * 8;
        if (transportRuntime.phase === "unloading") {
          // 各搬送ロボの unloading 位置に積み下ろす想定だが、当面は STORAGE_TILE 単一
          const storageScreen = tileToScreen(STORAGE_TILE.col, STORAGE_TILE.row);
          sprite.position.set(storageScreen.x, storageScreen.y - 4 - stackOffset);
          sprite.zIndex = depthOf(STORAGE_TILE.col, STORAGE_TILE.row) * 10 + 2 + slot;
          const p = Math.min(1, transportRuntime.unloadTimer / UNLOAD_DURATION_SEC);
          sprite.alpha = 1 - p;
        } else {
          sprite.position.set(tscreen.x, tscreen.y - 14 - stackOffset);
          sprite.zIndex = depthOf(transportRuntime.position.col, transportRuntime.position.row) * 10 + 2 + slot;
          sprite.alpha = 1;
        }
      });
    }

    // ワーク (各ワークの conveyorId からコンベア path を引いて位置補間)
    const presentIds = new Set<string>();
    for (const w of state.works) {
      presentIds.add(w.id);
      let sprite = workSprites.get(w.id);
      if (!sprite) {
        sprite = createWorkContainer(w);
        workSprites.set(w.id, sprite);
        layers.entityLayer.addChild(sprite.root);
      }
      if (sprite.lastPhase !== w.phase) {
        updateWorkColor(sprite, w);
        sprite.lastPhase = w.phase;
      }
      const pos = getWorkRenderTilePos(w, state);
      const screen = tileToScreen(pos.col, pos.row);
      const onBelt =
        w.phase === "pre_press" || w.phase === "pressing" || w.phase === "post_press" || w.phase === "in_buffer";
      const liftY = onBelt ? CONVEYOR_BELT_HEIGHT_PX : 0;
      const stackY = w.phase === "in_buffer" && w.bufferSlot !== null ? w.bufferSlot * 8 : 0;
      sprite.root.position.set(screen.x, screen.y - 4 - liftY - stackY);
      sprite.root.zIndex = depthOf(pos.col, pos.row) * 10 + 11 + (w.bufferSlot ?? 0);
      sprite.root.visible = w.phase !== "being_picked" && w.phase !== "on_transport" && w.phase !== "unloading";
    }

    for (const [id, sprite] of workSprites) {
      if (!presentIds.has(id)) {
        layers.entityLayer.removeChild(sprite.root);
        sprite.root.destroy({ children: true });
        workSprites.delete(id);
      }
    }
  }

  function dispose() {
    for (const s of workSprites.values()) {
      s.root.destroy({ children: true });
    }
    workSprites.clear();
    for (const c of layers.groundLayer.removeChildren()) {
      c.destroy({ children: true });
    }
    for (const c of layers.entityLayer.removeChildren()) {
      c.destroy({ children: true });
    }
  }

  return { update, dispose };
}

// ─── helpers ───────────────────────────────────────────

function drawGround(groundLayer: Container) {
  for (let row = 0; row < MAP_ROWS; row++) {
    for (let col = 0; col < MAP_COLS; col++) {
      const g = new Graphics();
      const color = (col + row) % 2 === 0 ? PALETTE.groundLight : PALETTE.groundDark;
      drawIsoTile(g, color, PALETTE.groundAccent);
      const { x, y } = tileToScreen(col, row);
      g.position.set(x, y);
      groundLayer.addChild(g);
    }
  }
}

/**
 * 機械の占有タイルを描画する (P3: scene の全オブジェクトを iterate)。
 */
function drawOccupationTiles(groundLayer: Container, state: SimulationState) {
  const scene = state.scene;

  // 搬送ロボ経路タイル + parking + unloading
  for (const t of scene.transports) {
    for (const p of t.route) {
      if (
        (p.col === t.parking.col && p.row === t.parking.row) ||
        (p.col === t.unloading.col && p.row === t.unloading.row)
      ) {
        continue;
      }
      const g = new Graphics();
      drawOccupationTile(g, PALETTE.occupationTransportPath, 0.3, PALETTE.occupationTransportPath, 0.7);
      const s = tileToScreen(p.col, p.row);
      g.position.set(s.x, s.y);
      groundLayer.addChild(g);
    }
    {
      const g = new Graphics();
      drawOccupationTile(g, PALETTE.occupationLoading, 0.45, PALETTE.occupationLoading, 1);
      const s = tileToScreen(t.parking.col, t.parking.row);
      g.position.set(s.x, s.y);
      groundLayer.addChild(g);
    }
    {
      const g = new Graphics();
      drawOccupationTile(g, PALETTE.occupationUnloading, 0.45, PALETTE.occupationUnloading, 1);
      const s = tileToScreen(t.unloading.col, t.unloading.row);
      g.position.set(s.x, s.y);
      groundLayer.addChild(g);
    }
  }

  // バッファ (各コンベアの path 末端)
  for (const c of scene.conveyors) {
    const last = c.path[c.path.length - 1];
    if (!last) continue;
    const g = new Graphics();
    drawOccupationTile(g, PALETTE.occupationBuffer, 0.35, PALETTE.occupationBuffer, 0.85);
    const s = tileToScreen(last.col, last.row);
    g.position.set(s.x, s.y);
    groundLayer.addChild(g);
  }

  // プレス機の占有 3 タイル (本体 + 北脚 + 南脚)
  for (const press of scene.presses) {
    for (const tile of [
      press.position,
      { col: press.position.col, row: press.position.row - 1 },
      { col: press.position.col, row: press.position.row + 1 },
    ]) {
      const g = new Graphics();
      drawOccupationTile(g, PALETTE.occupationPress, 0.4, PALETTE.occupationPress, 0.9);
      const s = tileToScreen(tile.col, tile.row);
      g.position.set(s.x, s.y);
      groundLayer.addChild(g);
    }
  }

  // アーム占有タイル
  for (const arm of scene.arms) {
    const g = new Graphics();
    drawOccupationTile(g, PALETTE.occupationArm, 0.4, PALETTE.occupationArm, 0.9);
    const s = tileToScreen(arm.position.col, arm.position.row);
    g.position.set(s.x, s.y);
    groundLayer.addChild(g);
  }
}

function drawUnloadArea(groundLayer: Container) {
  const g = createUnloadZone();
  const { x, y } = tileToScreen(STORAGE_TILE.col, STORAGE_TILE.row);
  g.position.set(x, y);
  groundLayer.addChild(g);

  for (let i = 0; i < 3; i++) {
    const box = new Graphics();
    box.roundRect(-4, -8, 8, 8, 1);
    box.fill(0xa67c00);
    box.roundRect(-4, -8, 8, 2, 1);
    box.fill(0xd4a000);
    const off = tileToScreen(STORAGE_TILE.col + (i - 1) * 0.3, STORAGE_TILE.row - 0.5);
    box.position.set(off.x, off.y);
    groundLayer.addChild(box);
  }
}

function createWorkContainer(w: Work): WorkSprite {
  const root = new Container();
  const body = createWorkSprite(workColorKind(w));
  root.addChild(body);
  return { root, body, lastPhase: w.phase };
}

function updateWorkColor(sprite: WorkSprite, w: Work) {
  const parent = sprite.root;
  parent.removeChild(sprite.body);
  sprite.body.destroy();
  sprite.body = createWorkSprite(workColorKind(w));
  parent.addChild(sprite.body);
}

function workColorKind(w: Work): "idle" | "pressed" | "done" {
  switch (w.phase) {
    case "pre_press":
    case "pressing":
      return "idle";
    case "post_press":
    case "in_buffer":
      return "pressed";
    default:
      return "done";
  }
}

/**
 * P3: ワークの描画タイル位置を、所属コンベアの path から決定する。
 */
function getWorkRenderTilePos(w: Work, state: SimulationState): TilePos {
  const conveyor = state.scene.conveyors.find((c) => c.id === w.conveyorId);
  if (!conveyor) return { col: 0, row: 0 };

  if (w.phase === "in_buffer" && w.bufferSlot !== null) {
    const last = conveyor.path[conveyor.path.length - 1];
    return last ?? { col: 0, row: 0 };
  }
  if (w.phase === "pre_press" || w.phase === "pressing" || w.phase === "post_press") {
    return tileAtPathDistance(conveyor.path, w.pathDist);
  }
  // being_picked / on_transport / unloading は visible:false なので位置は適当でよい
  // ただし配置ヒントとしてアームタイルへ寄せる
  const armScene = state.scene.arms[0];
  return armScene?.position ?? { col: 0, row: 0 };
}

// 未使用警告抑制 (将来の拡張用)
void pathDistanceOfTile;
